import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import EventForm from '../../../components/admin/events/EventForm';
import { useEvents } from '../../../hooks/useEvents';
import { getEventById, getCommissionSettings, updateCommissionSettings } from '../../../services/event.service';
import { Event } from '../../../types/event';
import { useToast } from '../../../hooks/useToast';

const EditEvent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { editEvent } = useEvents();
  const { showToast } = useToast();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commissionSettings, setCommissionSettings] = useState<{ platform_fee: number, payment_fee: number }>({ platform_fee: 0.05, payment_fee: 0.02 });
  const [isCommissionSubmitting, setIsCommissionSubmitting] = useState(false);
  
  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const eventData = await getEventById(id);
        setEvent(eventData);
        
        // Récupérer les paramètres de commission
        const commissionData = await getCommissionSettings(id);
        setCommissionSettings(commissionData);
      } catch (error: any) {
        showToast(error.message || 'Erreur lors du chargement de l\'événement', 'error');
        navigate('/admin/events');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvent();
  }, [id, navigate, showToast]);
  
  const handleSubmit = async (eventData: any) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      await editEvent(id, eventData);
      showToast('Événement mis à jour avec succès', 'success');
      navigate('/admin/events');
    } catch (error: any) {
      showToast(error.message || 'Erreur lors de la mise à jour de l\'événement', 'error');
      setIsSubmitting(false);
    }
  };
  
  const handleCommissionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    
    try {
      setIsCommissionSubmitting(true);
      await updateCommissionSettings(id, commissionSettings.platform_fee, commissionSettings.payment_fee);
      showToast('Paramètres de commission mis à jour avec succès', 'success');
    } catch (error: any) {
      showToast(error.message || 'Erreur lors de la mise à jour des paramètres de commission', 'error');
    } finally {
      setIsCommissionSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  if (!event) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  Événement non trouvé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <Link
            to="/events/admin"
            className="text-yellow-400 hover:text-yellow-300 flex items-center text-sm"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Retour aux événements
          </Link>
        </div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-100">Modifier l'événement</h1>
          <p className="mt-2 text-sm text-gray-300">
            Modifiez les informations de l'événement ci-dessous.
          </p>
        </div>
        
        <div className="bg-black/90 shadow rounded-lg p-6 border border-white/10 mb-8">
          <EventForm 
            initialData={event} 
            onSubmit={handleSubmit} 
            isSubmitting={isSubmitting} 
          />
        </div>
        
        {/* Section des paramètres de commission */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-100">Paramètres financiers</h2>
          <p className="mt-2 text-sm text-gray-300">
            Personnalisez les taux de commission pour cet événement.
          </p>
        </div>
        
        <div className="bg-black/90 shadow rounded-lg p-6 border border-white/10">
          <form onSubmit={handleCommissionSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="platform_fee" className="block text-sm font-medium text-gray-300 mb-1">
                  Commission plateforme (%)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="platform_fee"
                    className="block w-full rounded-md bg-white/5 border border-white/10 py-2 px-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                    value={commissionSettings.platform_fee * 100}
                    onChange={(e) => setCommissionSettings(prev => ({ ...prev, platform_fee: parseFloat(e.target.value) / 100 }))}
                    step="0.1"
                    min="0"
                    max="100"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-400">%</span>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-400">Commission prélevée par la plateforme sur chaque vente.</p>
              </div>
              
              <div>
                <label htmlFor="payment_fee" className="block text-sm font-medium text-gray-300 mb-1">
                  Frais de paiement (%)
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="payment_fee"
                    className="block w-full rounded-md bg-white/5 border border-white/10 py-2 px-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                    value={commissionSettings.payment_fee * 100}
                    onChange={(e) => setCommissionSettings(prev => ({ ...prev, payment_fee: parseFloat(e.target.value) / 100 }))}
                    step="0.1"
                    min="0"
                    max="100"
                    required
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-400">%</span>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-400">Frais liés au traitement des paiements.</p>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Aperçu des commissions</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Commission totale :</span>
                <span className="font-semibold text-white">{((commissionSettings.platform_fee + commissionSettings.payment_fee) * 100).toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-400">Pour un ticket de 10 000 FCFA :</span>
                <span className="font-semibold text-white">{(10000 * (commissionSettings.platform_fee + commissionSettings.payment_fee)).toLocaleString('fr-FR')} FCFA</span>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isCommissionSubmitting}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-yellow-400 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400 ${isCommissionSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isCommissionSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900 mr-2"></div>
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer les paramètres'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EditEvent;
